import { Chord, realizeRoman } from './roman';
import { Key } from './key';
import { Pitch, PitchClass, mod, pcNumber } from './pitch';

/**
 * Rule-based voice leading.
 *
 * Given a chord and the previous voicing, choose the voicing that moves least,
 * keeps the essential chord tones, resolves tendency tones and avoids parallel
 * fifths and octaves. Exhaustive and deterministic: the same input always gives
 * the same voicing.
 */

export interface VoiceOptions {
  /** Total number of voices including the bass. Default 4. */
  voices?: number;
  /** Previous voicing, ascending MIDI numbers, same length as `voices`. */
  prev?: number[] | null;
  /** The chord that produced `prev`, for resolving leading tones and sevenths. */
  prevChord?: Chord | null;
  /** Force this bass note. */
  bassMidi?: number;
  bassRange?: [number, number];
  upperRange?: [number, number];
  /** Keep the top voice on this note (re-harmonizing a given melody). */
  fixedTop?: number;
}

const NATURAL_PC = [0, 2, 4, 5, 7, 9, 11];

/** Spell a MIDI note using the chord's spelled members (falls back to sharps). */
export function spellMidi(midi: number, members: PitchClass[]): Pitch {
  const pc = mod(midi, 12);
  const m = members.find((x) => pcNumber(x) === pc);
  const letterTable = [0, 0, 1, 1, 2, 3, 3, 4, 4, 5, 5, 6];
  const alterTable = [0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0];
  const letter = m ? m.letter : letterTable[pc];
  const alter = m ? m.alter : alterTable[pc];
  const octave = Math.round((midi - NATURAL_PC[letter] - alter) / 12) - 1;
  return { letter, alter, octave };
}

function* combinations(items: number[], k: number, start = 0, acc: number[] = []): Generator<number[]> {
  if (acc.length === k) {
    yield acc.slice();
    return;
  }
  for (let i = start; i <= items.length - (k - acc.length); i++) {
    acc.push(items[i]);
    yield* combinations(items, k, i + 1, acc);
    acc.pop();
  }
}

function pickBass(chord: Chord, opts: VoiceOptions): number {
  if (opts.bassMidi !== undefined) return opts.bassMidi;
  const [lo, hi] = opts.bassRange ?? [38, 55];
  const target = opts.prev ? opts.prev[0] : 48;
  const pc = pcNumber(chord.bass);
  let best = lo;
  let bestDist = Infinity;
  for (let m = lo; m <= hi; m++) {
    if (mod(m, 12) !== pc) continue;
    const d = Math.abs(m - target);
    if (d < bestDist) {
      best = m;
      bestDist = d;
    }
  }
  return best;
}

export function scoreVoicing(v: number[], chord: Chord, opts: VoiceOptions): number {
  const { prev, prevChord } = opts;
  let score = 0;
  const upper = v.slice(1);

  // Motion
  if (prev && prev.length === v.length) {
    for (let i = 1; i < v.length; i++) score += Math.abs(v[i] - prev[i]) * (i === v.length - 1 ? 1.3 : 1);
  } else {
    score += Math.abs(upper[upper.length - 1] - 74) * 0.3 + (upper[upper.length - 1] - upper[0]) * 0.1;
  }

  // Coverage of chord members
  const counts = new Map<number, number>();
  for (const m of v) counts.set(mod(m, 12), (counts.get(mod(m, 12)) ?? 0) + 1);
  const [root, third, fifth, seventh] = chord.members.map(pcNumber);
  if (chord.members.length > 4 || (chord.members.length === 4 && !chord.seventh)) {
    // Augmented sixths and other lists: every member matters.
    for (const pc of chord.pcs) if (!counts.has(pc)) score += 10;
  } else {
    if (!counts.has(root)) score += 14;
    if (third !== undefined && !counts.has(third)) score += 12;
    if (seventh !== undefined && !counts.has(seventh)) score += 9;
    if (fifth !== undefined && !counts.has(fifth)) score += 0.8;
  }

  // Doublings
  const leadingTone = mod(pcNumber(chord.localKey.tonic) - 1, 12);
  counts.forEach((n, pc) => {
    if (n < 2) return;
    if (chord.fn === 'D' && pc === leadingTone) score += 8 * (n - 1);
    if (seventh !== undefined && pc === seventh) score += 10 * (n - 1);
    if (pc === third) score += 1 * (n - 1);
    if (pc === fifth) score += 0.5 * (n - 1);
    if (n >= 3) score += 3 * (n - 2);
  });

  // Spacing
  for (let i = 1; i < upper.length; i++) if (upper[i] - upper[i - 1] > 12) score += 6;
  if (upper[0] - v[0] > 24) score += 3;
  if (upper[upper.length - 1] > 81) score += 3;

  if (prev && prev.length === v.length) {
    // Parallel fifths and octaves (including the bass)
    for (let i = 0; i < v.length; i++) {
      for (let j = i + 1; j < v.length; j++) {
        const mi = v[i] - prev[i];
        const mj = v[j] - prev[j];
        if (mi === 0 || mj === 0 || Math.sign(mi) !== Math.sign(mj)) continue;
        const before = mod(prev[j] - prev[i], 12);
        const after = mod(v[j] - v[i], 12);
        if ((before === 7 && after === 7) || (before === 0 && after === 0)) score += 25;
      }
    }
    // Overlaps
    for (let i = 1; i < v.length - 1; i++) if (v[i] > prev[i + 1] || v[i + 1] < prev[i]) score += 2;

    // Tendency tones of the previous chord
    if (prevChord) {
      const prevLeading = mod(pcNumber(prevChord.localKey.tonic) - 1, 12);
      const goal = pcNumber(prevChord.localKey.tonic);
      const prevSeventh = prevChord.seventh ? pcNumber(prevChord.members[3]) : null;
      for (let i = 1; i < v.length; i++) {
        const weight = i === v.length - 1 ? 6 : 3;
        if (prevChord.fn === 'D' && mod(prev[i], 12) === prevLeading && chord.pcs.includes(goal) && v[i] - prev[i] !== 1) score += weight;
        if (prevSeventh !== null && mod(prev[i], 12) === prevSeventh) {
          const step = v[i] - prev[i];
          if (!(step === -1 || step === -2 || (step === 0 && chord.pcs.includes(prevSeventh)))) score += weight;
        }
      }
    }
  }
  return score;
}

/** Voice a chord. Returns ascending MIDI numbers, bass first. */
export function voiceChord(chord: Chord, opts: VoiceOptions = {}): number[] {
  const voices = opts.voices ?? 4;
  const bass = pickBass(chord, opts);
  const [lo, hi] = opts.upperRange ?? [52, 81];
  const candidates: number[] = [];
  for (let m = Math.max(lo, bass + 3); m <= hi; m++) if (chord.pcs.includes(mod(m, 12))) candidates.push(m);
  if (opts.fixedTop !== undefined && !candidates.includes(opts.fixedTop)) candidates.push(opts.fixedTop);
  candidates.sort((a, b) => a - b);
  let best: number[] | null = null;
  let bestScore = Infinity;
  for (const combo of combinations(candidates, voices - 1)) {
    if (opts.fixedTop !== undefined && combo[combo.length - 1] !== opts.fixedTop) continue;
    const v = [bass, ...combo];
    const s = scoreVoicing(v, chord, opts);
    if (s < bestScore - 1e-9) {
      best = v;
      bestScore = s;
    }
  }
  if (!best) throw new Error(`No voicing found for ${chord.symbol}`);
  return best;
}

export interface VoicedChord {
  chord: Chord;
  midi: number[];
  pitches: Pitch[];
}

/** Voice a whole progression of Roman numerals, optionally over a fixed bass line. */
export function voiceProgression(
  symbols: string[],
  key: Key,
  opts: { voices?: number; bass?: (number | undefined)[]; start?: number[] } = {},
): VoicedChord[] {
  const out: VoicedChord[] = [];
  let prev: number[] | null = opts.start ?? null;
  let prevChord: Chord | null = null;
  symbols.forEach((symbol, i) => {
    const chord = realizeRoman(symbol, key);
    const midi = voiceChord(chord, { voices: opts.voices, prev, prevChord, bassMidi: opts.bass?.[i] });
    out.push({ chord, midi, pitches: midi.map((m) => spellMidi(m, chord.members)) });
    prev = midi;
    prevChord = chord;
  });
  return out;
}

/** Parallel perfect intervals between two successive voicings, for tests and teaching. */
export function findParallels(a: number[], b: number[]): Array<{ voices: [number, number]; interval: 'fifth' | 'octave' }> {
  const out: Array<{ voices: [number, number]; interval: 'fifth' | 'octave' }> = [];
  for (let i = 0; i < a.length; i++) {
    for (let j = i + 1; j < a.length; j++) {
      const mi = b[i] - a[i];
      const mj = b[j] - a[j];
      if (mi === 0 || mj === 0 || Math.sign(mi) !== Math.sign(mj)) continue;
      const before = mod(a[j] - a[i], 12);
      const after = mod(b[j] - b[i], 12);
      if (before === 7 && after === 7) out.push({ voices: [i, j], interval: 'fifth' });
      if (before === 0 && after === 0) out.push({ voices: [i, j], interval: 'octave' });
    }
  }
  return out;
}
